// src/pages/performance/SelfReviewAppraisalPage.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";

import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Divider,
  TextField,
  Chip,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";

import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SendIcon from "@mui/icons-material/Send";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoIcon from "@mui/icons-material/Info";

/* ---------------------------------------
   API
---------------------------------------- */
const API = {
  showAppraisal: (id) => `/performance/appraisals/${id}`,
  submitStep: (appraisalId, stepId) =>
    `/performance/appraisals/${appraisalId}/steps/${stepId}`,
};

/* ---------------------------------------
   Status chips
---------------------------------------- */
const STATUS_LABELS = {
  draft: "Draft",
  pending: "Pending",
  in_progress: "In progress",
  completed: "Completed",
  cancelled: "Cancelled",
};
const STATUS_COLOR = {
  draft: "default",
  pending: "warning",
  in_progress: "info",
  completed: "success",
  cancelled: "error",
};

/* ---------------------------------------
   Helpers
---------------------------------------- */
const safeMsg = (err, fallback = "Something went wrong") =>
  err?.response?.data?.message || err?.message || fallback;

const formatDateTime = (value) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

function getSteps(appraisal) {
  return appraisal?.stepInstances || appraisal?.step_instances || [];
}

/**
 * This page is specifically for Employee Self Review:
 * pick the step where step user == employee (prefer pending / in_progress),
 * otherwise fallback to current step order / pending.
 */
function findSelfReviewStep(appraisal) {
  const steps = getSteps(appraisal);
  if (!steps.length) return null;

  const empCode = appraisal?.employee?.employee_code || appraisal?.employee_code;
  const self = steps
    .filter((s) => String(s?.user?.employee_code || "") === String(empCode || ""))
    .sort((a, b) => (Number(a.step_order) || 0) - (Number(b.step_order) || 0));

  const selfPending =
    self.find((s) => String(s.status) === "pending") ||
    self.find((s) => String(s.status) === "in_progress") ||
    self.find((s) => String(s.status) === "not_started");

  if (selfPending) return selfPending;

  const currentOrder = appraisal?.current_step_order;
  if (currentOrder != null) {
    const byOrder = steps.find((s) => Number(s.step_order) === Number(currentOrder));
    if (byOrder) return byOrder;
  }

  return steps.find((s) => String(s.status) === "pending") || steps[0] || null;
}

/* -------- Q/A normalization (supports answers / responses / seeded) -------- */
const getAnswerRating = (a) => a?.rating ?? a?.score ?? a?.value ?? a?.points ?? null;
const getAnswerApplicability = (a) =>
  a?.applicability ?? a?.is_applicable ?? a?.isApplicable ?? null;
const getAnswerComment = (a) =>
  a?.comment ?? a?.comments ?? a?.remark ?? a?.remarks ?? a?.note ?? a?.notes ?? "";

const normalizeApplicability = (v) => {
  if (v == null || v === "") return "YES";
  if (typeof v === "boolean") return v ? "YES" : "NO";
  const s = String(v).trim().toLowerCase();
  if (["1", "true", "yes", "y"].includes(s)) return "YES";
  if (["0", "false", "no", "n"].includes(s)) return "NO";
  return String(v).toUpperCase();
};

const extractQuestionId = (x) =>
  x?.question_id ?? x?.appraisal_question_id ?? x?.question?.id ?? null;

const extractQuestionTitle = (x) =>
  x?.question?.title || x?.question?.name || x?.question_title || x?.title || "Question";

const normalizeQAItem = (item) => {
  const questionId = extractQuestionId(item);
  const title = extractQuestionTitle(item);

  const rating = getAnswerRating(item);
  const app = normalizeApplicability(getAnswerApplicability(item));
  const comment = getAnswerComment(item) || "";

  const id = item?.id ?? `${questionId || "q"}-${title}-${String(rating ?? "x")}-${app}`;
  return { id, questionId, title, rating, app, comment };
};

const pickPrefillSubmission = (step) =>
  step?.latest_submission || step?.latestSubmission || step?.seed_submission || null;

const buildQAFromStep = (step) => {
  const sub = pickPrefillSubmission(step);

  // 1) From submission answers
  const answers = Array.isArray(sub?.answers) ? sub.answers : [];
  if (answers.length) return answers.map(normalizeQAItem);

  // 2) From step responses
  const responses = Array.isArray(step?.responses) ? step.responses : [];
  if (responses.length) return responses.map(normalizeQAItem);

  return [];
};

const getSubmissionComment = (sub) =>
  sub?.summary_comment ??
  sub?.comment ??
  sub?.comments ??
  sub?.notes ??
  sub?.remark ??
  sub?.remarks ??
  "";

/* ---------------------------------------
   Page
---------------------------------------- */
export default function SelfReviewAppraisalPage() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [appraisal, setAppraisal] = useState(null);
  const [activeStep, setActiveStep] = useState(null);

  const [comment, setComment] = useState("");
  const [qaItems, setQaItems] = useState([]); // editable state

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");

  const borderColor = theme.hr?.line?.soft || alpha(theme.palette.divider, 0.16);
  const headerBg = alpha(theme.palette.primary.main, theme.palette.mode === "light" ? 0.05 : 0.12);

  const statusChipProps = useMemo(() => {
    const status = appraisal?.status || "draft";
    return {
      label: STATUS_LABELS[status] || status,
      color: STATUS_COLOR[status] || "default",
      variant: "outlined",
      sx: { borderRadius: "8px", fontWeight: 800 },
    };
  }, [appraisal]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const res = await api.get(API.showAppraisal(id));
      const payload = res?.data?.data ?? res?.data;

      const step = findSelfReviewStep(payload);

      setAppraisal(payload || null);
      setActiveStep(step || null);

      // Prefill Q/A + comment
      const prefillSub = pickPrefillSubmission(step);
      const prefillComment = getSubmissionComment(prefillSub);

      setQaItems(buildQAFromStep(step));
      setComment(prefillComment || "");
    } catch (err) {
      setLoadError(safeMsg(err, "Failed to load appraisal"));
      setAppraisal(null);
      setActiveStep(null);
      setQaItems([]);
      setComment("");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const employee = useMemo(() => appraisal?.employee || {}, [appraisal]);
  const workflow = useMemo(
    () => appraisal?.workflowTemplate || appraisal?.workflow_template || {},
    [appraisal]
  );

  const isSelfReview = useMemo(() => {
    const empCode = employee?.employee_code || appraisal?.employee_code;
    const stepUserCode = activeStep?.user?.employee_code;
    return !!empCode && !!stepUserCode && String(empCode) === String(stepUserCode);
  }, [employee, activeStep, appraisal]);

  const prefillMeta = useMemo(() => {
    const sub = pickPrefillSubmission(activeStep);
    const isSeed = !activeStep?.latest_submission && !!activeStep?.seed_submission;
    return {
      hasPrefill: !!sub,
      isSeed,
      version: sub?.version ?? null,
      updatedAt: sub?.updated_at ?? sub?.submitted_at ?? sub?.created_at ?? null,
      isDraft: !!sub?.is_draft,
    };
  }, [activeStep]);

  const handleBack = () => navigate("/performance/appraisals/my-tasks");

  const updateQA = (idx, patch) => {
    setQaItems((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!appraisal || !activeStep) return;

    setSubmitting(true);
    setSubmitError("");
    setSubmitSuccess("");

    try {
      const cleanedAnswers = (qaItems || [])
        .map((x) => ({
          question_id: x.questionId,
          rating: x.rating === "" ? null : x.rating,
          applicability: x.app || "YES",
          comment: x.comment || null,
        }))
        .filter((x) => x.question_id != null); // keep only valid

      // ✅ comment-only allowed after backend fix
      const payload = {
        summary_comment: comment?.trim() ? comment.trim() : null,
      };

      // Only send answers if we have any questions (safe even if empty is allowed)
      if (cleanedAnswers.length) payload.answers = cleanedAnswers;

      const res = await api.post(API.submitStep(appraisal.id, activeStep.id), payload);

      setSubmitSuccess(res?.data?.message || "Self review submitted.");
      await fetchData();
    } catch (err) {
      setSubmitError(safeMsg(err, "Failed to submit self review"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: "100%", display: "flex", alignItems: "center", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 }, display: "flex", justifyContent: "center" }}>
        <Paper sx={{ p: 2, maxWidth: 720, width: "100%", borderRadius: "8px", border: `1px solid ${borderColor}` }}>
          <Stack spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              Failed to load self review
            </Typography>
            <Alert severity="error" sx={{ borderRadius: "8px" }}>{loadError}</Alert>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button variant="outlined" onClick={fetchData} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 800 }}>
                Retry
              </Button>
              <Button variant="text" onClick={handleBack} sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 800 }}>
                Back
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  if (!appraisal || !activeStep) {
    return (
      <Box sx={{ p: { xs: 2, md: 4 } }}>
        <Alert severity="info" sx={{ borderRadius: "8px" }}>No step found.</Alert>
        <Button sx={{ mt: 2, borderRadius: "8px", textTransform: "none", fontWeight: 800 }} startIcon={<ArrowBackIcon />} onClick={handleBack}>
          Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 1.25, md: 2 },
        minHeight: "100vh",
        background:
          theme.hr?.gradient?.page ||
          `linear-gradient(180deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${theme.palette.background.default} 55%)`,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, md: 2 },
          borderRadius: "8px",
          border: `1px solid ${borderColor}`,
          backgroundColor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.96 : 0.9),
          boxShadow: "none",
          overflow: "hidden",
          maxWidth: 1180,
          mx: "auto",
        }}
      >
        {/* Header */}
        <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Button
              size="small"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ borderRadius: "8px", textTransform: "none", fontWeight: 800 }}
            >
              Back
            </Button>

            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, mb: 0.25, lineHeight: 1.1 }}>
                Self Review · Appraisal #{appraisal.id}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {workflow?.name || "Appraisal"} · Step #{activeStep.step_order ?? "—"} ·{" "}
                {activeStep.step_name || activeStep.name || "Self review"}
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Refresh">
              <span>
                <IconButton
                  onClick={fetchData}
                  size="small"
                  disabled={submitting}
                  sx={{
                    borderRadius: "8px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                  }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Chip size="small" {...statusChipProps} />
          </Stack>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        {!isSelfReview && (
          <Alert severity="warning" sx={{ mb: 1.5, borderRadius: "8px" }}>
            This step is not a self review for the employee. Please go back to tasks.
          </Alert>
        )}

        {/* Employee block */}
        <Paper
          variant="outlined"
          sx={{
            p: 1.5,
            borderRadius: "8px",
            border: `1px solid ${borderColor}`,
            backgroundColor: headerBg,
            mb: 1.5,
          }}
        >
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} justifyContent="space-between">
            <Box>
              <Typography variant="subtitle2" sx={{ color: "text.secondary", mb: 0.75, fontWeight: 800 }}>
                Employee
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 950 }}>
                {employee?.name || "—"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Code: {employee?.employee_code || "—"} · Department: {employee?.department || "—"}
              </Typography>
            </Box>

            <Box sx={{ minWidth: 260 }}>
              <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 800 }}>
                Latest data source
              </Typography>
              {prefillMeta.hasPrefill ? (
                <Typography variant="body2" sx={{ fontWeight: 800 }}>
                  {prefillMeta.isSeed ? "Seeded from previous submission" : "Latest submission"}{" "}
                  {prefillMeta.version != null ? `· V${prefillMeta.version}` : ""}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No previous submission
                </Typography>
              )}
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 0.25 }}>
                Updated: {formatDateTime(prefillMeta.updatedAt)}
                {prefillMeta.isDraft ? " · Draft" : ""}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Alerts */}
        <Stack spacing={1.5}>
          {submitError && (
            <Alert severity="error" onClose={() => setSubmitError("")} sx={{ borderRadius: "8px" }}>
              {submitError}
            </Alert>
          )}
          {submitSuccess && (
            <Alert severity="success" onClose={() => setSubmitSuccess("")} sx={{ borderRadius: "8px" }}>
              {submitSuccess}
            </Alert>
          )}
        </Stack>

        {/* Questions & values */}
        <Paper variant="outlined" sx={{ borderRadius: "8px", p: 1.5, mt: 1.5, borderColor }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
            <InfoIcon sx={{ color: "text.secondary" }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 950 }}>
              Questions & values
            </Typography>
            <Chip
              size="small"
              variant="outlined"
              label={`${qaItems.length} item${qaItems.length === 1 ? "" : "s"}`}
              sx={{ borderRadius: "8px", fontWeight: 800 }}
            />
          </Stack>

          {qaItems.length === 0 ? (
            <Alert severity="info" icon={<InfoIcon />} sx={{ borderRadius: "8px" }}>
              No questions found for this step yet (no submission answers / responses available).
            </Alert>
          ) : (
            <Stack spacing={1}>
              {qaItems.map((q, idx) => (
                <Accordion
                  key={q.id}
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: "8px",
                    border: `1px solid ${alpha(theme.palette.divider, 0.14)}`,
                    "&:before": { display: "none" },
                    overflow: "hidden",
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 950 }}>
                        {idx + 1}. {q.title}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Applicability: {q.app || "—"} · Rating: {q.rating == null || q.rating === "" ? "—" : q.rating}
                      </Typography>
                    </Stack>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Stack spacing={1.25}>
                      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
                        <TextField
                          label="Applicability"
                          select
                          fullWidth
                          value={q.app || "YES"}
                          onChange={(e) => updateQA(idx, { app: e.target.value })}
                          SelectProps={{ native: true }}
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        >
                          <option value="YES">YES</option>
                          <option value="NO">NO</option>
                        </TextField>

                        <TextField
                          label="Rating"
                          type="number"
                          fullWidth
                          value={q.rating ?? ""}
                          onChange={(e) => updateQA(idx, { rating: e.target.value === "" ? null : Number(e.target.value) })}
                          inputProps={{ step: 1 }}
                          placeholder="e.g. 1 - 5"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              borderRadius: "8px",
                            },
                          }}
                        />
                      </Stack>

                      <TextField
                        label="Comment"
                        placeholder="Write a comment for this question (optional)"
                        fullWidth
                        multiline
                        minRows={3}
                        value={q.comment || ""}
                        onChange={(e) => updateQA(idx, { comment: e.target.value })}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                          },
                        }}
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          )}
        </Paper>

        {/* Summary comment */}
        <Paper variant="outlined" sx={{ borderRadius: "8px", p: 1.5, mt: 1.5, borderColor }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 950, mb: 1 }}>
            Your comment
          </Typography>

          <TextField
            label="Self review comment"
            placeholder="Write your self review here..."
            fullWidth
            multiline
            minRows={5}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />

          <Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              disabled={submitting}
              sx={{ borderRadius: "8px", fontWeight: 800, textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              variant="contained"
              startIcon={submitting ? <CircularProgress size={16} /> : <SendIcon />}
              onClick={handleSubmit}
              disabled={submitting || !isSelfReview}
              sx={{ borderRadius: "8px", fontWeight: 900, textTransform: "none" }}
            >
              Submit self review
            </Button>
          </Stack>
        </Paper>
      </Paper>
    </Box>
  );
}
